const DEFAULT_FILTER_CONFIG = {
  searchPlaceholder: "Search tracker records...",
  type: {
    label: "Type",
    allLabel: "All Types",
    options: [],
    matchers: [/\btype\b/i, /category/i, /classification/i, /\bgroup\b/i],
  },
  status: {
    label: "Status",
    allLabel: "All Statuses",
    options: [],
    matchers: [/status/i, /\bstate\b/i, /approval/i, /progress/i, /result/i],
  },
  date: {
    startLabel: "Start Date",
    endLabel: "End Date",
    helperLabel: "Date filter uses",
    matchers: [/date/i, /deadline/i, /period/i, /\byear\b/i, /\bmonth\b/i],
  },
};

const DEFAULT_REGISTRATION_FORM = {
  title: "Tracker Registration",
  subtitle: "Capture a new tracker record for the selected programme.",
  submitLabel: "Register Record",
  successMessage: "Record registered successfully!",
  sections: [
    {
      title: "Record Details",
      fields: [
        { name: "recordTitle", label: "Record Title", type: "text", required: true, span: "full", placeholder: "Enter record title" },
        { name: "recordType", label: "Record Type", type: "select", required: true, options: ["Primary", "Secondary", "Follow-up", "Exception"] },
        { name: "recordStatus", label: "Status", type: "select", required: true, options: ["Draft", "In Progress", "Completed", "Deferred"] },
        { name: "recordDate", label: "Record Date", type: "date", required: true },
        { name: "responsibleOfficer", label: "Responsible Officer", type: "text", placeholder: "Enter officer name" },
        { name: "contactNumber", label: "Contact Number", type: "tel", placeholder: "0XX XXX XXXX" },
        { name: "location", label: "Location", type: "text", placeholder: "Enter location" },
        { name: "notes", label: "Notes", type: "textarea", span: "full", placeholder: "Add any supporting notes" },
      ],
    },
  ],
};

const TRACKER_PROFILE_TEMPLATES = {
  generic: {
    profileKey: "generic",
    defaultTrackerLabel: "Tracker",
    filterConfig: DEFAULT_FILTER_CONFIG,
    registration: DEFAULT_REGISTRATION_FORM,
  },
  planningImplementation: {
    profileKey: "planningImplementation",
    defaultTrackerLabel: "Planning and Implementation Tracker",
    filterConfig: {
      searchPlaceholder: "Search activities, officers, locations, or implementation notes...",
      type: {
        label: "Activity Type",
        allLabel: "All Activity Types",
        options: ["Plan", "Activity", "Intervention", "Capacity Building", "Climate Action"],
        matchers: [/activity/i, /plan/i, /intervention/i, /initiative/i, /programme?/i],
      },
      status: {
        label: "Implementation Status",
        allLabel: "All Implementation Statuses",
        options: ["Planned", "In Progress", "Completed", "Delayed", "Cancelled"],
        matchers: [/implementation/i, /status/i, /progress/i, /stage/i, /completion/i],
      },
      date: {
        startLabel: "Start Date",
        endLabel: "End Date",
        helperLabel: "Implementation date uses",
        matchers: [/start date/i, /activity date/i, /implementation date/i, /deadline/i, /period/i],
      },
    },
    registration: {
      title: "Register Implementation Record",
      subtitle: "Capture a planning or implementation entry for the selected tracker.",
      submitLabel: "Register Record",
      successMessage: "Implementation record registered successfully!",
      sections: [
        {
          title: "Activity Details",
          fields: [
            { name: "activityTitle", label: "Activity Title", type: "text", required: true, span: "full", placeholder: "Enter activity title" },
            { name: "activityType", label: "Activity Type", type: "select", required: true, options: ["Plan", "Activity", "Intervention", "Capacity Building", "Climate Action"] },
            { name: "implementationStatus", label: "Implementation Status", type: "select", required: true, options: ["Planned", "In Progress", "Completed", "Delayed", "Cancelled"] },
            { name: "startDate", label: "Start Date", type: "date", required: true },
            { name: "endDate", label: "End Date", type: "date" },
            { name: "responsibleUnit", label: "Responsible Unit", type: "text", placeholder: "Enter responsible unit" },
            { name: "focalPerson", label: "Focal Person", type: "text", placeholder: "Enter focal person" },
            { name: "budgetAmount", label: "Budget Amount", type: "number", placeholder: "0.00" },
            { name: "location", label: "Location", type: "text", placeholder: "Enter implementation location" },
            { name: "notes", label: "Implementation Notes", type: "textarea", span: "full", placeholder: "Add milestones, risks, or next steps" },
          ],
        },
      ],
    },
  },
  projectMonitoring: {
    profileKey: "projectMonitoring",
    defaultTrackerLabel: "Project Monitoring Tracker",
    filterConfig: {
      searchPlaceholder: "Search projects, contractors, supervisors, or locations...",
      type: {
        label: "Project Type",
        allLabel: "All Project Types",
        options: ["Infrastructure", "Procurement", "Service Delivery", "Programme", "Capital Project"],
        matchers: [/project type/i, /project category/i, /category/i, /component/i],
      },
      status: {
        label: "Project Status",
        allLabel: "All Project Statuses",
        options: ["Planned", "Mobilised", "In Progress", "Completed", "Suspended"],
        matchers: [/project status/i, /implementation status/i, /progress/i, /completion/i],
      },
      date: {
        startLabel: "From Date",
        endLabel: "To Date",
        helperLabel: "Project date uses",
        matchers: [/start date/i, /update date/i, /expected completion/i, /completion date/i, /inspection date/i],
      },
    },
    registration: {
      title: "Register Project Record",
      subtitle: "Capture a project or programme monitoring record for the selected tracker.",
      submitLabel: "Register Project",
      successMessage: "Project record registered successfully!",
      sections: [
        {
          title: "Project Overview",
          fields: [
            { name: "projectTitle", label: "Project Title", type: "text", required: true, span: "full", placeholder: "Enter project title" },
            { name: "projectType", label: "Project Type", type: "select", required: true, options: ["Infrastructure", "Procurement", "Service Delivery", "Programme", "Capital Project"] },
            { name: "projectStatus", label: "Project Status", type: "select", required: true, options: ["Planned", "Mobilised", "In Progress", "Completed", "Suspended"] },
            { name: "startDate", label: "Start Date", type: "date", required: true },
            { name: "expectedCompletionDate", label: "Expected Completion", type: "date" },
            { name: "contractor", label: "Contractor", type: "text", placeholder: "Enter contractor or implementing partner" },
            { name: "supervisor", label: "Supervisor", type: "text", placeholder: "Enter supervising officer" },
            { name: "budgetAmount", label: "Budget Amount", type: "number", placeholder: "0.00" },
            { name: "projectLocation", label: "Project Location", type: "text", placeholder: "Enter project location" },
            { name: "notes", label: "Project Notes", type: "textarea", span: "full", placeholder: "Add project observations or progress notes" },
          ],
        },
      ],
    },
  },
  serviceDelivery: {
    profileKey: "serviceDelivery",
    defaultTrackerLabel: "Service Delivery Tracker",
    filterConfig: {
      searchPlaceholder: "Search facilities, providers, service areas, or locations...",
      type: {
        label: "Service Type",
        allLabel: "All Service Types",
        options: ["Facility", "Inspection", "Support", "Utility", "Provider"],
        matchers: [/service type/i, /facility type/i, /category/i, /provider type/i, /inspection type/i],
      },
      status: {
        label: "Service Status",
        allLabel: "All Service Statuses",
        options: ["Operational", "Pending", "Under Review", "Completed", "Non-Compliant"],
        matchers: [/service status/i, /status/i, /compliance/i, /availability/i, /condition/i],
      },
      date: {
        startLabel: "Start Date",
        endLabel: "End Date",
        helperLabel: "Service date uses",
        matchers: [/inspection date/i, /assessment date/i, /service date/i, /review date/i, /date/i],
      },
    },
    registration: {
      title: "Register Service Record",
      subtitle: "Capture a service delivery, facility, or inspection record for the selected tracker.",
      submitLabel: "Register Service",
      successMessage: "Service record registered successfully!",
      sections: [
        {
          title: "Service Details",
          fields: [
            { name: "facilityName", label: "Facility or Service Name", type: "text", required: true, span: "full", placeholder: "Enter facility or service name" },
            { name: "serviceType", label: "Service Type", type: "select", required: true, options: ["Facility", "Inspection", "Support", "Utility", "Provider"] },
            { name: "serviceStatus", label: "Service Status", type: "select", required: true, options: ["Operational", "Pending", "Under Review", "Completed", "Non-Compliant"] },
            { name: "inspectionDate", label: "Inspection or Assessment Date", type: "date", required: true },
            { name: "managerName", label: "Manager or Contact Person", type: "text", placeholder: "Enter manager or contact person" },
            { name: "phoneNumber", label: "Phone Number", type: "tel", placeholder: "0XX XXX XXXX" },
            { name: "serviceLocation", label: "Location", type: "text", placeholder: "Enter service location" },
            { name: "capacity", label: "Capacity", type: "number", placeholder: "0" },
            { name: "remarks", label: "Remarks", type: "textarea", span: "full", placeholder: "Add condition notes, compliance details, or follow-up actions" },
          ],
        },
      ],
    },
  },
  governanceCompliance: {
    profileKey: "governanceCompliance",
    defaultTrackerLabel: "Governance and Compliance Tracker",
    filterConfig: {
      searchPlaceholder: "Search records, responsible officers, departments, or actions...",
      type: {
        label: "Record Type",
        allLabel: "All Record Types",
        options: ["Audit", "Charter", "Compliance Review", "Publication", "Service Assessment"],
        matchers: [/record type/i, /service area/i, /audit/i, /charter/i, /publication/i],
      },
      status: {
        label: "Compliance Status",
        allLabel: "All Compliance Statuses",
        options: ["Pending", "Compliant", "Non-Compliant", "In Review", "Closed"],
        matchers: [/compliance/i, /status/i, /resolution/i, /approval/i, /closure/i],
      },
      date: {
        startLabel: "From Date",
        endLabel: "To Date",
        helperLabel: "Review date uses",
        matchers: [/review date/i, /record date/i, /issue date/i, /submission date/i, /publication date/i],
      },
    },
    registration: {
      title: "Register Governance Record",
      subtitle: "Capture a governance, audit, or compliance record for the selected tracker.",
      submitLabel: "Register Record",
      successMessage: "Governance record registered successfully!",
      sections: [
        {
          title: "Record Details",
          fields: [
            { name: "recordTitle", label: "Record Title", type: "text", required: true, span: "full", placeholder: "Enter record title" },
            { name: "recordType", label: "Record Type", type: "select", required: true, options: ["Audit", "Charter", "Compliance Review", "Publication", "Service Assessment"] },
            { name: "complianceStatus", label: "Compliance Status", type: "select", required: true, options: ["Pending", "Compliant", "Non-Compliant", "In Review", "Closed"] },
            { name: "reviewDate", label: "Review Date", type: "date", required: true },
            { name: "responsibleOfficer", label: "Responsible Officer", type: "text", placeholder: "Enter responsible officer" },
            { name: "department", label: "Department or Unit", type: "text", placeholder: "Enter department or unit" },
            { name: "referenceNumber", label: "Reference Number", type: "text", placeholder: "Enter reference number" },
            { name: "actionOwner", label: "Action Owner", type: "text", placeholder: "Enter action owner" },
            { name: "dueDate", label: "Due Date", type: "date" },
            { name: "notes", label: "Findings or Notes", type: "textarea", span: "full", placeholder: "Add findings, observations, or actions required" },
          ],
        },
      ],
    },
  },
  committeeTracker: {
    profileKey: "committeeTracker",
    defaultTrackerLabel: "Committee Tracker",
    filterConfig: {
      searchPlaceholder: "Search committees, chairpersons, venues, or meeting details...",
      type: {
        label: "Committee Type",
        allLabel: "All Committee Types",
        options: ["Sub-Structure", "Executive", "Audit", "Petition", "General Assembly"],
        matchers: [/committee/i, /meeting type/i, /structure/i, /assembly/i],
      },
      status: {
        label: "Meeting Status",
        allLabel: "All Meeting Statuses",
        options: ["Scheduled", "Held", "Adjourned", "Completed", "Cancelled"],
        matchers: [/meeting status/i, /status/i, /attendance/i, /decision/i],
      },
      date: {
        startLabel: "Meeting From",
        endLabel: "Meeting To",
        helperLabel: "Meeting date uses",
        matchers: [/meeting date/i, /date/i, /session date/i],
      },
    },
    registration: {
      title: "Register Committee Record",
      subtitle: "Capture a committee or meeting record for the selected tracker.",
      submitLabel: "Register Meeting",
      successMessage: "Committee record registered successfully!",
      sections: [
        {
          title: "Committee Meeting Details",
          fields: [
            { name: "committeeName", label: "Committee Name", type: "text", required: true, span: "full", placeholder: "Enter committee name" },
            { name: "committeeType", label: "Committee Type", type: "select", required: true, options: ["Sub-Structure", "Executive", "Audit", "Petition", "General Assembly"] },
            { name: "meetingStatus", label: "Meeting Status", type: "select", required: true, options: ["Scheduled", "Held", "Adjourned", "Completed", "Cancelled"] },
            { name: "meetingDate", label: "Meeting Date", type: "date", required: true },
            { name: "chairperson", label: "Chairperson", type: "text", placeholder: "Enter chairperson" },
            { name: "secretary", label: "Secretary", type: "text", placeholder: "Enter secretary" },
            { name: "venue", label: "Venue", type: "text", placeholder: "Enter venue" },
            { name: "attendance", label: "Attendance", type: "number", placeholder: "0" },
            { name: "agenda", label: "Agenda", type: "textarea", span: "full", placeholder: "Enter meeting agenda" },
            { name: "decisions", label: "Decisions or Action Points", type: "textarea", span: "full", placeholder: "Enter decisions or action points" },
          ],
        },
      ],
    },
  },
  businessSupport: {
    profileKey: "businessSupport",
    defaultTrackerLabel: "Business Support Tracker",
    filterConfig: {
      searchPlaceholder: "Search beneficiaries, support types, locations, or support notes...",
      type: {
        label: "Support Type",
        allLabel: "All Support Types",
        options: ["Training", "Grant", "Engagement", "Advisory", "Facility Support"],
        matchers: [/support type/i, /beneficiary type/i, /engagement type/i, /business type/i, /training/i],
      },
      status: {
        label: "Support Status",
        allLabel: "All Support Statuses",
        options: ["Planned", "Approved", "Delivered", "In Progress", "Closed"],
        matchers: [/support status/i, /status/i, /delivery/i, /approval/i, /engagement/i],
      },
      date: {
        startLabel: "Support From",
        endLabel: "Support To",
        helperLabel: "Support date uses",
        matchers: [/support date/i, /engagement date/i, /training date/i, /delivery date/i, /date/i],
      },
    },
    registration: {
      title: "Register Support Record",
      subtitle: "Capture a beneficiary or business support record for the selected tracker.",
      submitLabel: "Register Support",
      successMessage: "Support record registered successfully!",
      sections: [
        {
          title: "Beneficiary Details",
          fields: [
            { name: "beneficiaryName", label: "Beneficiary Name", type: "text", required: true, span: "full", placeholder: "Enter beneficiary name" },
            { name: "beneficiaryType", label: "Beneficiary Type", type: "select", required: true, options: ["Individual", "Group", "Association", "Business", "Facility"] },
            { name: "supportType", label: "Support Type", type: "select", required: true, options: ["Training", "Grant", "Engagement", "Advisory", "Facility Support"] },
            { name: "supportStatus", label: "Support Status", type: "select", required: true, options: ["Planned", "Approved", "Delivered", "In Progress", "Closed"] },
            { name: "supportDate", label: "Support Date", type: "date", required: true },
            { name: "contactPerson", label: "Contact Person", type: "text", placeholder: "Enter contact person" },
            { name: "phoneNumber", label: "Phone Number", type: "tel", placeholder: "0XX XXX XXXX" },
            { name: "businessLocation", label: "Business or Support Location", type: "text", placeholder: "Enter location" },
            { name: "notes", label: "Support Notes", type: "textarea", span: "full", placeholder: "Add support description, outcomes, or follow-up needs" },
          ],
        },
      ],
    },
  },
  performanceAssessment: {
    profileKey: "performanceAssessment",
    defaultTrackerLabel: "Performance Assessment Tracker",
    filterConfig: {
      searchPlaceholder: "Search assessments, assessors, districts, or evidence references...",
      type: {
        label: "Assessment Area",
        allLabel: "All Assessment Areas",
        options: ["Governance", "Service Delivery", "Infrastructure", "Compliance", "Finance"],
        matchers: [/assessment area/i, /indicator/i, /theme/i, /category/i, /performance area/i],
      },
      status: {
        label: "Assessment Status",
        allLabel: "All Assessment Statuses",
        options: ["Draft", "In Review", "Validated", "Approved", "Closed"],
        matchers: [/assessment status/i, /status/i, /validation/i, /approval/i, /score/i],
      },
      date: {
        startLabel: "Assessment From",
        endLabel: "Assessment To",
        helperLabel: "Assessment date uses",
        matchers: [/assessment date/i, /review date/i, /validation date/i, /date/i],
      },
    },
    registration: {
      title: "Register Assessment Record",
      subtitle: "Capture a performance or assessment record for the selected tracker.",
      submitLabel: "Register Assessment",
      successMessage: "Assessment record registered successfully!",
      sections: [
        {
          title: "Assessment Details",
          fields: [
            { name: "assessmentTitle", label: "Assessment Title", type: "text", required: true, span: "full", placeholder: "Enter assessment title" },
            { name: "assessmentArea", label: "Assessment Area", type: "select", required: true, options: ["Governance", "Service Delivery", "Infrastructure", "Compliance", "Finance"] },
            { name: "assessmentStatus", label: "Assessment Status", type: "select", required: true, options: ["Draft", "In Review", "Validated", "Approved", "Closed"] },
            { name: "assessmentDate", label: "Assessment Date", type: "date", required: true },
            { name: "assessorName", label: "Assessor Name", type: "text", placeholder: "Enter assessor name" },
            { name: "score", label: "Score", type: "number", placeholder: "0" },
            { name: "evidenceReference", label: "Evidence Reference", type: "text", placeholder: "Enter evidence reference" },
            { name: "districtName", label: "District", type: "text", placeholder: "Enter district name" },
            { name: "notes", label: "Assessment Notes", type: "textarea", span: "full", placeholder: "Add findings, evidence summary, or validation notes" },
          ],
        },
      ],
    },
  },
};

const TRACKER_PROFILE_ALIASES = {
  ArLnAxhykoz: "planningImplementation",
  rpqTh4RQMSq: "planningImplementation",
  p1ccS2ROn0Q: "projectMonitoring",
  YHVtzXj8iIC: "projectMonitoring",
  g3wMUKEMmH3: "projectMonitoring",
  nGFVo65uUE4: "serviceDelivery",
  g27TeeehRQC: "serviceDelivery",
  cKOJDISfpX2: "serviceDelivery",
  OiDekszWx2p: "serviceDelivery",
  Txcfc03kUCi: "serviceDelivery",
  abiQOocP8YA: "serviceDelivery",
  ng9HEemUaIM: "serviceDelivery",
  F7wJn9wdwcQ: "serviceDelivery",
  X5kGqVpbGoN: "governanceCompliance",
  GciA0HJcRzN: "governanceCompliance",
  Z3qMezPtpEb: "governanceCompliance",
  NdogHaFyDuI: "committeeTracker",
  nBX5Jeo69up: "businessSupport",
  yiLy1Tiyjng: "businessSupport",
  zordSafiO6O: "businessSupport",
  Sqzqe1y30hF: "businessSupport",
  Ch38jUWJpUR: "performanceAssessment",
  n8WIhwDrAO7: "performanceAssessment",
  vkJZ5R2mSJ3: "performanceAssessment",
  p1vYbSWkgyD: "performanceAssessment",
  mAEretIhuqM: "performanceAssessment",
  AJDfCnHCQ2j: "performanceAssessment",
  w2au8V5taU8: "performanceAssessment",
  joUkaNeiZ0O: "performanceAssessment",
  RwWtjFaorvN: "performanceAssessment",
  R5MX47LvztN: "performanceAssessment",
  pYvmkB4s6Nq: "performanceAssessment",
  dYNmYGtArrK: "performanceAssessment",
  SERtHvYVHRT: "performanceAssessment",
};

function mergeFilterConfig(filterConfig) {
  return {
    ...DEFAULT_FILTER_CONFIG,
    ...filterConfig,
    type: {
      ...DEFAULT_FILTER_CONFIG.type,
      ...(filterConfig?.type || {}),
    },
    status: {
      ...DEFAULT_FILTER_CONFIG.status,
      ...(filterConfig?.status || {}),
    },
    date: {
      ...DEFAULT_FILTER_CONFIG.date,
      ...(filterConfig?.date || {}),
    },
  };
}

export function getTrackerProfile(trackerId, trackerLabel = "") {
  const profileKey = TRACKER_PROFILE_ALIASES[trackerId] || "generic";
  const profileTemplate = TRACKER_PROFILE_TEMPLATES[profileKey] || TRACKER_PROFILE_TEMPLATES.generic;
  const resolvedLabel = trackerLabel || profileTemplate.defaultTrackerLabel || "Tracker";

  return {
    ...profileTemplate,
    trackerId,
    trackerLabel: resolvedLabel,
    filterConfig: mergeFilterConfig(profileTemplate.filterConfig),
    registration: {
      ...DEFAULT_REGISTRATION_FORM,
      ...(profileTemplate.registration || {}),
    },
  };
}

export function buildTrackerRegistrationInitialValues(profile) {
  const safeProfile = profile || TRACKER_PROFILE_TEMPLATES.generic;
  const sections = safeProfile?.registration?.sections || [];

  return sections.reduce((acc, section) => {
    (section?.fields || []).forEach((field) => {
      acc[field.name] = field.defaultValue || "";
    });
    return acc;
  }, {});
}

export function validateTrackerRegistrationForm(profile, formData) {
  const errors = {};
  const sections = profile?.registration?.sections || [];

  sections.forEach((section) => {
    (section?.fields || []).forEach((field) => {
      const rawValue = formData?.[field.name];
      const value = typeof rawValue === "string" ? rawValue.trim() : rawValue;

      if (field.required && !value) {
        errors[field.name] = `${field.label} is required`;
        return;
      }

      if (!value) return;

      if (field.type === "email" && !/\S+@\S+\.\S+/.test(String(value))) {
        errors[field.name] = "Please enter a valid email address";
      }

      if (field.type === "tel") {
        const normalizedPhone = String(value).replace(/\s/g, "");
        if (!/^\+?\d{7,15}$/.test(normalizedPhone)) {
          errors[field.name] = "Please enter a valid phone number";
        }
      }

      if (field.type === "number" && Number.isNaN(Number(value))) {
        errors[field.name] = `${field.label} must be a valid number`;
      }
    });
  });

  return errors;
}

export function getTrackerSelectOptions(values = []) {
  return values.map((value, index) => {
    if (value && typeof value === "object") {
      const label =
        value?.label ||
        value?.displayName ||
        value?.name ||
        value?.code ||
        value?.value ||
        `Option ${index + 1}`;
      const normalizedValue =
        value?.value ??
        value?.code ??
        value?.displayName ??
        value?.name ??
        label;

      return {
        label,
        value: normalizedValue,
      };
    }

    return {
      label: value,
      value,
    };
  });
}
